import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowRight,
  BadgeCheck,
  CalendarCheck,
  ClipboardCheck,
  FileCheck,
  HandHeart,
  Shield,
  Target,
} from 'lucide-react';

const values = [
  { icon: FileCheck, title: 'Evidence before approval', description: 'Key decisions should be backed by clear records before work moves forward.' },
  { icon: ClipboardCheck, title: 'Clear readiness blockers', description: 'Teams need plain-English signals about what is ready, missing or due for review.' },
  { icon: CalendarCheck, title: 'Safer staffing decisions', description: 'Worker readiness, role fit and attendance evidence should support every staffing decision.' },
  { icon: HandHeart, title: 'Respectful care delivery', description: 'Operational controls should protect dignity, continuity and person-centred support.' },
  { icon: Shield, title: 'Accountability', description: 'Clear ownership, timestamps and review history help teams explain what happened and why.' },
  { icon: Target, title: 'Reliability', description: 'Dependable processes reduce last-minute surprises for providers, workers and people receiving care.' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-semibold text-primary mb-6">
              <BadgeCheck className="h-4 w-4" />
              Osabea CareOps, shaped by Osabea Healthcare Solutions Ltd
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-6">
              Built from real care operations.
            </h1>
            <p className="text-lg text-text-muted leading-relaxed">
              Osabea CareOps is being developed by Osabea Healthcare Solutions Ltd to help care teams organise worker readiness, service-user evidence, staffing decisions, attendance review and audit trails in one connected workspace.
            </p>
            <p className="mt-5 text-text-muted leading-relaxed">
              Osabea Healthcare Solutions Ltd is the first tenant and operating workspace. Wider company access is invite-only while subscription setup and tenant controls are managed by the CareOps owner.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary mb-4">
              Operating principles behind CareOps
            </h2>
            <p className="text-lg text-text-muted">
              These principles guide the platform design and keep the experience practical for busy, non-technical care teams.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, idx) => (
              <div key={value.title} className="card-public" data-testid={`value-card-${idx}`}>
                <div className="w-14 h-14 bg-accent rounded-2xl flex items-center justify-center mb-6">
                  <value.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">{value.title}</h3>
                <p className="text-text-muted">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary mb-6">
                One owner layer, many workplace tenants.
              </h2>
              <p className="text-lg text-text-muted mb-6">
                CareOps separates the platform owner view from each provider workspace. That keeps subscription setup and company invites under central control.
              </p>
              <p className="text-text-muted mb-8">
                New companies are prepared as workplace drafts first. External logins should only be activated after operational tenant isolation is fully enforced across the portal.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-primary hover:bg-primary-hover text-white rounded-full" data-testid="about-request-invite-btn">
                  <Link to="/contact?type=platform_demo">
                    Request Invite
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/5 rounded-full" data-testid="about-login-btn">
                  <Link to="/login">
                    Workplace Login
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/6129589/pexels-photo-6129589.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Healthcare professionals discussing operations"
                className="rounded-3xl shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-[#E4E8EB] bg-white p-8 lg:p-12">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-primary mb-3">Subscription direction</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary mb-5">
                  Enough in Essential, room to grow.
                </h2>
                <p className="text-text-muted leading-relaxed">
                  Essential should be useful on day one: applicant onboarding, checks, document tracking, training evidence and a basic audit trail. Professional, Enterprise and add-ons can carry the advanced and higher-volume workflows.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Worker readiness and evidence visibility',
                  'Service-user setup basics',
                  'Shift, attendance and timesheet context',
                  'Audit preparation without regulatory outcome guarantees',
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-[#E4E8EB] bg-[#F8FAFA] p-5">
                    <FileCheck className="h-5 w-5 text-primary mb-3" />
                    <p className="text-sm font-medium text-text-primary leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
            Ready to see Osabea CareOps?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Request invite-only access or use your workplace login if your organisation has already been set up.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-secondary hover:bg-white/90 rounded-full px-8 h-14">
              <Link to="/contact?type=platform_demo">Request Invite</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 h-14">
              <Link to="/login">Workplace Login</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
